import { logger } from '../utils/logger';
import { CheckoutStill } from '../components/stageshow/Stills';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    useTheme,
    CircularProgress,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Divider,
    Alert,
    Button,
    Chip,
    LinearProgress,
    Stack,
} from '@mui/material';
import {
    TrendingUp,
    Receipt,
    Warning,
    Refresh,
    ArrowUpward,
    ArrowDownward,
    Business,
    ListAlt,
    Group,
    HelpOutline,
    Schedule,
    LocationOn,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNexus } from '../contexts/NexusContext';
import { useTax } from '../contexts/TaxContext';
import { TaxStatus } from '../types/tax';
import { OnboardingStatus, activationOf, onboardingService } from '../services/onboardingService';
import { ActivationChecklist } from '../components/onboarding/ActivationChecklist';
import { activationComplete } from '../components/onboarding/steps';
import { ImportTransactionsDialog } from '../components/ImportTransactionsDialog';
import platformService from '../services/platformService';
import nexusService from '../services/nexusService';
import { companyService } from '../services/companyService';
import { buildHolisticStats } from '../utils/holisticStats';
import { AppPage, AppSection, DeveloperPrompt, EmptyState, FigureRow, PlanPrompt } from '../components/app/AppPage';

const Dashboard: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { refreshNexusStatus, refreshing: nexusRefreshing, error: nexusError } = useNexus();
    const {
        taxStatus,
        loading: taxLoading,
        error: taxError,
        refreshTaxStatus,
        refreshing: taxRefreshing,
    } = useTax();
    const [statsRefreshing, setStatsRefreshing] = useState(false);

    const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
    const [onboardingLoading, setOnboardingLoading] = useState(false);
    const [onboardingError, setOnboardingError] = useState<string | null>(null);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [dashboardStats, setDashboardStats] = useState<any>(null);
    const [hasAnimated, setHasAnimated] = useState(false);
    const [complianceSummary, setComplianceSummary] = useState<{
        rate: number;
        pending: number;
        total: number;
        calculated: number;
        failed: number;
    } | null>(null);
    const [complianceLoading, setComplianceLoading] = useState(false);
    const [nexusInsightsSummary, setNexusInsightsSummary] = useState<{
        exceeded: number;
        approaching: number;
        safe: number;
        highlightedStates: Array<{ code: string; status: string; percent: number }>;
        totalStatesMonitored?: number;
        lastUpdated?: string;
        analysisWindow?: string;
    } | null>(null);
    const [nexusSummaryLoading, setNexusSummaryLoading] = useState(false);
    const [effectiveCompanyId, setEffectiveCompanyId] = useState<number | null>(null);
    const [effectiveCompanyName, setEffectiveCompanyName] = useState<string | null>(null);
    const calendarYear = useMemo(() => new Date().getFullYear(), []);
    const salesTaxStatus = useMemo(() => {
        if (complianceSummary) {
            if (complianceSummary.total > 0 && complianceSummary.pending === 0) {
                return 'Complete';
            }
            if (complianceSummary.pending > 0) {
                return 'Processing';
            }
            if (complianceSummary.total === 0) {
                return 'Awaiting Data';
            }
        }
        return taxStatus?.status || 'Unknown';
    }, [complianceSummary, taxStatus?.status]);

    const processedCount = useMemo(
        () => (complianceSummary ? complianceSummary.calculated + complianceSummary.failed : 0),
        [complianceSummary],
    );
    const isRefreshing =
        statsRefreshing ||
        nexusRefreshing ||
        taxRefreshing ||
        complianceLoading ||
        nexusSummaryLoading;

    useEffect(() => {
        if (!hasAnimated) {
            setHasAnimated(true);
        }
    }, [hasAnimated]);

    // Note: refreshTaxStatus is already called by TaxContext on mount
    // No need to call it again here to avoid duplicate API calls

    // Check onboarding status from the backend (the checklist and the API activity panel read it)
    const loadOnboardingStatus = useCallback(async () => {
        if (!user) {
            setOnboardingStatus(null);
            return;
        }
        setOnboardingLoading(true);
        setOnboardingError(null);
        try {
            setOnboardingStatus(await onboardingService.status());
        } catch (error) {
            logger.error('[Dashboard] Failed to load onboarding status:', error);
            setOnboardingError('Could not load your setup progress. Refresh to try again.');
        } finally {
            setOnboardingLoading(false);
        }
    }, [user]);

    useEffect(() => {
        void loadOnboardingStatus();
    }, [loadOnboardingStatus]);

    useEffect(() => {
        let isCancelled = false;

        const resolveCompanyContext = async () => {
            if (!user) {
                setEffectiveCompanyId(null);
                setEffectiveCompanyName(null);
                return;
            }

            const assignCompany = (id: number | null, name: string | null) => {
                if (!isCancelled) {
                    setEffectiveCompanyId(id);
                    setEffectiveCompanyName(name);
                }
            };

            if (user.primary_company_id) {
                assignCompany(user.primary_company_id, null);
                try {
                    const company = await companyService.getCompany(user.primary_company_id);
                    if (!isCancelled) {
                        setEffectiveCompanyName(company?.name ?? null);
                    }
                } catch (error) {
                    logger.error('[Dashboard] Failed to load primary company name:', error);
                }
                return;
            }

            try {
                const companies = await companyService.listCompanies();
                if (isCancelled) return;
                if (companies.length) {
                    // Prefer primary company, otherwise first created; but if another company has nexus activity, pick it.
                    const defaultCompany = companies
                        .slice()
                        .sort(
                            (a, b) =>
                                new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
                        )[0];
                    let chosen = defaultCompany;

                    // Try to find a company with visible nexus states to show accurate summary on the dashboard
                    for (const company of companies) {
                        try {
                            const summary = await nexusService.fetchNexusDashboardSummary(company.id, {
                                rangeMode: 'calendar_year',
                                calendarYear,
                            });
                            if ((summary?.total_states_monitored ?? 0) > 0) {
                                chosen = company;
                                break;
                            }
                        } catch (err) {
                            logger.warn('[Dashboard] Unable to fetch nexus summary for company', company.id, err);
                        }
                    }

                    assignCompany(chosen.id, chosen.name);
                } else {
                    assignCompany(null, null);
                }
            } catch (error) {
                if (!isCancelled) {
                    logger.error('[Dashboard] Failed to resolve default company from user_companies:', error);
                    assignCompany(null, null);
                }
            }
        };

        void resolveCompanyContext();
        return () => {
            isCancelled = true;
        };
    }, [user, calendarYear]);

    // Fetch dashboard stats
    useEffect(() => {
        const fetchDashboardStats = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token || !user) return;
                
                const response = await fetch('/api/v1/dashboard/stats', {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                    }
                });
                
                if (response.ok) {
                    const stats = await response.json();
                    setDashboardStats(stats);
                    logger.log('[Dashboard] Fetched stats:', stats);
                }
            } catch (error) {
                logger.error('[Dashboard] Failed to fetch dashboard stats:', error);
            }
        };
        
        fetchDashboardStats();
    }, [user]);

    const fetchComplianceSummary = useCallback(async () => {
        if (!user) {
            setComplianceSummary(null);
            return;
        }
        try {
            setComplianceLoading(true);
            const integrations = await platformService.listIntegrations();
            const stats = buildHolisticStats(integrations);
            const totalFailed = Object.values(stats.platformBreakdown).reduce(
                (sum, breakdown) => sum + Number(breakdown.failedTax || 0),
                0,
            );
            const processed = stats.totalTaxCalculated + totalFailed;
            const rate =
                stats.totalTransactions > 0
                    ? Math.round((processed / stats.totalTransactions) * 100)
                    : 0;
            setComplianceSummary({
                rate,
                pending: stats.totalPendingTax,
                total: stats.totalTransactions,
                calculated: stats.totalTaxCalculated,
                failed: totalFailed,
            });
        } catch (error) {
            logger.error('[Dashboard] Failed to compute compliance summary:', error);
            setComplianceSummary(null);
        } finally {
            setComplianceLoading(false);
        }
    }, [user]);

    const fetchNexusInsightsSummary = useCallback(async () => {
        if (!user) {
            setNexusInsightsSummary(null);
            return;
        }
        const targetCompanyId = effectiveCompanyId ?? user.primary_company_id ?? null;
        if (!targetCompanyId) {
            setNexusInsightsSummary(null);
            return;
        }
        setNexusSummaryLoading(true);
        try {
            const summaryResponse = await nexusService.fetchNexusDashboardSummary(targetCompanyId, {
                rangeMode: 'calendar_year',
                calendarYear,
            });
            const highlightedStates = Array.isArray(summaryResponse?.highlighted_states)
                ? summaryResponse.highlighted_states
                      .map((entry: any) => ({
                          code: entry.state_code,
                          status: (entry.status || '').toLowerCase() || 'safe',
                          percent: Number(entry.percent_to_threshold ?? 0) || 0,
                      }))
                      .filter((entry: { code: string }) => Boolean(entry.code))
                : [];
            const exceeded = summaryResponse?.states_with_nexus ?? 0;
            const approaching = summaryResponse?.states_approaching ?? 0;
            const total = summaryResponse?.total_states_monitored ?? highlightedStates.length;
            const safe = Math.max(total - (exceeded + approaching), 0);

            setNexusInsightsSummary({
                exceeded,
                approaching,
                safe,
                highlightedStates,
                totalStatesMonitored: total,
                lastUpdated: summaryResponse?.last_updated ?? undefined,
                analysisWindow: summaryResponse?.analysis_window?.label,
            });
        } catch (error) {
            logger.error('[Dashboard] Failed to fetch nexus dashboard summary:', error);
            setNexusInsightsSummary(null);
        } finally {
            setNexusSummaryLoading(false);
        }
    }, [effectiveCompanyId, user]);

    useEffect(() => {
        if (!user) {
            return;
        }
        void fetchComplianceSummary();
        void fetchNexusInsightsSummary();
    }, [user, fetchComplianceSummary, fetchNexusInsightsSummary]);

    const handleRefresh = async () => {
        if (isRefreshing) {
            return;
        }
        setStatsRefreshing(true);
        try {
            await Promise.all([
                nexusRefreshing ? Promise.resolve() : refreshNexusStatus(),
                taxRefreshing ? Promise.resolve() : refreshTaxStatus(),
                (async () => {
                    const token = localStorage.getItem('token');
                    if (!token) return;
                    const response = await fetch('/api/v1/dashboard/stats', {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'X-API-Key': process.env.REACT_APP_API_KEY || 'test-key-123'
                        }
                    });
                    if (response.ok) {
                        const stats = await response.json();
                        setDashboardStats(stats);
                    }
                })(),
                fetchComplianceSummary(),
                fetchNexusInsightsSummary(),
                loadOnboardingStatus()
            ]);
        } finally {
            setStatsRefreshing(false);
        }
    };

    const money = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const firstName = (user?.full_name || '').trim().split(/\s+/)[0];

    // The API checklist step ticks when a billable call has come through the public API.
    const api = activationOf(onboardingStatus);
    const apiLastCall = api.last_call_at
        ? new Date(api.last_call_at).toLocaleString('en-US', {
              month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
          })
        : 'None yet';
    const setupComplete = onboardingStatus ? activationComplete(onboardingStatus) : false;

    const nexus = nexusInsightsSummary;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <AppPage
                title={firstName ? `Welcome back, ${firstName}` : 'Dashboard'}
                description={
                    effectiveCompanyName
                        ? `Sales tax for ${effectiveCompanyName}, calendar year ${calendarYear}.`
                        : `Your sales tax across every company, calendar year ${calendarYear}.`
                }
                actions={
                    <>
                        <Tooltip title="Refresh data">
                            <span>
                                <IconButton onClick={handleRefresh} disabled={isRefreshing} aria-label="Refresh data">
                                    <Refresh sx={{ color: isRefreshing ? 'text.disabled' : 'text.secondary' }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Button variant="outlined" startIcon={<ListAlt />} onClick={() => navigate('/complete-tax-view')}>
                            Complete Tax View
                        </Button>
                        <Button variant="outlined" startIcon={<Business />} onClick={() => navigate('/companies')}>
                            Companies
                        </Button>
                        <Button variant="contained" startIcon={<Group />} onClick={() => setShowImportDialog(true)}>
                            Import Transactions
                        </Button>
                    </>
                }
            >
                {(nexusError || taxError) && (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<Refresh />}>
                                Retry
                            </Button>
                        }
                    >
                        {nexusError || taxError}
                    </Alert>
                )}

                {(taxLoading || isRefreshing) && <LinearProgress aria-label="Refreshing dashboard" />}

                {!setupComplete && (
                    <AppSection
                        title="Finish setting up"
                        description="Each step reads from your account and ticks itself off."
                        action={
                            <Button size="small" variant="text" onClick={() => navigate('/onboarding')}>
                                Open setup guide
                            </Button>
                        }
                    >
                        <div className="ss-app-split">
                            <ActivationChecklist
                                status={onboardingStatus}
                                loading={onboardingLoading}
                                error={onboardingError}
                                onRetry={loadOnboardingStatus}
                                celebrate={false}
                            />
                            <div className="ss-app-still"><CheckoutStill /></div>
                        </div>
                    </AppSection>
                )}

                <AppSection
                    title="API activity"
                    description="Billable calls to the public API this month."
                    action={
                        <Button size="small" variant="text" onClick={() => navigate('/developers/console')}>
                            Open developer console
                        </Button>
                    }
                >
                    {onboardingLoading && !onboardingStatus ? (
                        <LinearProgress aria-label="Loading API activity" />
                    ) : (
                        <Stack spacing={2}>
                            <FigureRow
                                items={[
                                    { label: 'Test calls', value: api.test_calls.toLocaleString(), note: 'Never billed' },
                                    { label: 'Live calls', value: api.live_calls.toLocaleString(), note: 'Count toward the free month' },
                                    {
                                        label: 'Last call',
                                        value: apiLastCall,
                                        tone: api.first_call_done ? 'ok' : undefined,
                                    },
                                ]}
                            />
                            {api.first_call_done ? (
                                <Typography variant="body2" color="text.secondary">
                                    Your key is working: {api.calls_total.toLocaleString()} call
                                    {api.calls_total === 1 ? '' : 's'} received. Test calls never count toward the
                                    free monthly limit.
                                </Typography>
                            ) : (
                                <EmptyState
                                    title="No API calls yet"
                                    body="Create a test key, paste the request, and your first calculation shows up here."
                                    action={{ label: 'Go to the setup guide', onClick: () => navigate('/onboarding') }}
                                    secondary={{ label: 'Create a test key', to: '/developers/console' }}
                                />
                            )}
                        </Stack>
                    )}
                </AppSection>

                <AppSection title="This year so far" description="All companies" flat>
                    <FigureRow
                        items={[
                            { label: 'Total revenue', value: dashboardStats?.total_revenue != null ? money(dashboardStats.total_revenue) : 'No data' },
                            { label: 'Total tax', value: dashboardStats?.total_tax != null ? money(dashboardStats.total_tax) : 'No data' },
                            {
                                label: 'Tax calculated',
                                value: complianceSummary ? `${complianceSummary.rate}%` : 'No data',
                                note: complianceSummary
                                    ? `${processedCount.toLocaleString()} of ${complianceSummary.total.toLocaleString()} transactions${complianceSummary.failed ? `, ${complianceSummary.failed.toLocaleString()} failed` : ''}`
                                    : undefined,
                            },
                        ]}
                    />
                </AppSection>

                <div className="gt-split">
                    <AppSection
                        title="Economic nexus"
                        description={`${effectiveCompanyName ? `${effectiveCompanyName}, ` : ''}calendar year ${calendarYear}`}
                        action={
                            <Button size="small" variant="text" startIcon={<LocationOn />} onClick={() => navigate('/nexus/dashboard')}>
                                Open nexus dashboard
                            </Button>
                        }
                    >
                        {nexusSummaryLoading ? (
                            <LinearProgress aria-label="Loading nexus summary" />
                        ) : nexus ? (
                            <Stack spacing={3}>
                                <FigureRow
                                    items={[
                                        { label: 'Exceeded', value: nexus.exceeded, tone: nexus.exceeded > 0 ? 'error' : undefined },
                                        { label: 'Approaching', value: nexus.approaching, tone: nexus.approaching > 0 ? 'warn' : undefined },
                                        { label: 'Under threshold', value: nexus.safe, tone: 'ok' },
                                    ]}
                                />
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        States with sales
                                    </Typography>
                                    {nexus.highlightedStates.length ? (
                                        <ul className="gt-statelist">
                                            {nexus.highlightedStates.map((state) => {
                                                const chipColor = state.status === 'exceeded' ? 'error' : state.status === 'approaching' ? 'warning' : 'default';
                                                const statusLabel = state.status.charAt(0).toUpperCase() + state.status.slice(1);
                                                return (
                                                    <li key={state.code}>
                                                        <Chip
                                                            label={`${state.code} ${Math.round(state.percent)}% (${statusLabel})`}
                                                            size="small"
                                                            variant="outlined"
                                                            color={chipColor as any}
                                                        />
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No states are approaching economic nexus thresholds yet.
                                        </Typography>
                                    )}
                                </Box>
                                {nexus.totalStatesMonitored !== undefined ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {`Monitoring ${nexus.totalStatesMonitored} states${nexus.analysisWindow ? ` (${nexus.analysisWindow})` : ''}${
                                            nexus.lastUpdated ? `, updated ${new Date(nexus.lastUpdated).toLocaleString()}` : ''
                                        }. Matches the Economic Nexus Dashboard for ${effectiveCompanyName || 'your primary company'}.`}
                                    </Typography>
                                ) : null}
                            </Stack>
                        ) : (
                            <EmptyState
                                title="No nexus data yet"
                                body="Import transactions and GoTruTax measures your sales against every state’s threshold."
                                action={{ label: 'Import transactions', onClick: () => setShowImportDialog(true) }}
                                secondary={{ label: 'Connect a store', to: '/platforms/integrations' }}
                            />
                        )}
                    </AppSection>

                    <Stack spacing={3}>
                        <AppSection
                            title="Sales tax calculation"
                            action={
                                <Button size="small" variant="text" onClick={() => navigate('/complete-tax-view')}>
                                    Open complete tax view
                                </Button>
                            }
                        >
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>{salesTaxStatus}</Typography>
                            {complianceSummary ? (
                                <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {`${complianceSummary.rate}% of ${complianceSummary.total.toLocaleString()} transactions have completed tax calculation.`}
                                    </Typography>
                                    <LinearProgress value={complianceSummary.rate} variant="determinate" sx={{ mt: 1.5, mb: 1 }} aria-label="Tax calculation progress" />
                                    <Typography variant="caption" color="text.secondary">
                                        {`${processedCount.toLocaleString()} processed (${complianceSummary.calculated.toLocaleString()} successful${complianceSummary.failed ? `, ${complianceSummary.failed.toLocaleString()} failed` : ''}), ${complianceSummary.pending.toLocaleString()} pending`}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Compliance insights appear once transactions are imported.
                                </Typography>
                            )}
                        </AppSection>
                        <DeveloperPrompt />
                        <Button variant="text" startIcon={<HelpOutline />} onClick={() => navigate('/contact')} sx={{ alignSelf: 'flex-start' }}>
                            Get Help
                        </Button>
                    </Stack>
                </div>

                <PlanPrompt />
            </AppPage>
            <ImportTransactionsDialog
                open={showImportDialog}
                onClose={() => setShowImportDialog(false)}
                onComplete={handleRefresh}
            />
        </Box>
    );
};

export default Dashboard; 

